import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../api/client";
import CoinsRedeemBox from "../components/checkout/CoinsRedeemBox";
import LoadingScreen from "../components/ui/Loading/Loading";
import Breadcrumb from "../components/ui/Breadcrumb/Breadcrumb";
import { firstSupportedMethod, getCheckoutMethodAvailability } from "../utils/methodAvailability";
import { getConsultationTitle, createConsultationCheckout, getConsultationDetails } from "../api/consultations";
import "../components/shop/Checkout.css";

function getDefaultTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function toApiDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export default function ConsultationCheckout() {
  const { consultationRef } = useParams();
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();

  const [consultation, setConsultation] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    scheduled_at: "",
    timezone: getDefaultTimezone(),
  });
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [verifyBlock, setVerifyBlock] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [coinsData, setCoinsData] = useState({
    coins_to_use: 0,
    coins_discount: 0,
    final_total: 0,
    max_usable_coins: 0,
  });

  const submitLock = useRef(false);
  const messageRef = useRef(null);

  const consultationSalePrice = Number(consultation?.sale_price ?? 0);
  const consultationPrice = consultationSalePrice > 0 ? consultationSalePrice : (Number(consultation?.price) || 0);
  const total = Number(consultationPrice) || 0;

  const checkoutSubtotal = useMemo(() => {
    return Number(total.toFixed(2));
  }, [total]);

  const previewDiscount = Number(coinsData?.coins_discount || 0);

  const PLATFORM_FEE_RATE = 0.01;
  const STRIPE_FEE_RATE = 0.029;
  const STRIPE_FIXED_FEE = 0.30;

  const amountAfterCoins = Number(Math.max(0, checkoutSubtotal - previewDiscount).toFixed(2));
  const platformFeeAmount = Number((amountAfterCoins * PLATFORM_FEE_RATE).toFixed(2));
  const subtotalBeforeProcessor = Number((amountAfterCoins + platformFeeAmount).toFixed(2));

  const processorFeeAmount = subtotalBeforeProcessor > 0
    ? Number((((subtotalBeforeProcessor + STRIPE_FIXED_FEE) / (1 - STRIPE_FEE_RATE)) - subtotalBeforeProcessor).toFixed(2))
    : 0;

  const checkoutGrandTotal = Number((subtotalBeforeProcessor + processorFeeAmount).toFixed(2));

  const safeApiCall = async (callback) => {
    try {
      const res = await callback();
      if (!res || typeof res !== "object") {
        return {
          status: "error",
          message: isAr ? "استجابة غير صالحة من السيرفر." : "Invalid server response.",
          body: res ?? null,
        };
      }
      if (!Object.prototype.hasOwnProperty.call(res, "status")) {
        return { ...res, status: "success", message: res.message || "" };
      }
      return res;
    } catch (err) {
      const message =
        err?.message || (isAr ? "فشل الاتصال بالسيرفر." : "Server connection failed.");
      return {
        status: "error",
        message,
        body: err?.body ?? null,
      };
    }
  };

  const pushMessage = (text) => {
    setMsg(text);
    window.requestAnimationFrame(() => {
      messageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const validateCheckoutForm = () => {
    const name = (form.name || "").trim();
    const email = (form.email || "").trim();
    const phone = (form.phone || "").trim();
    const scheduled_at = (form.scheduled_at || "").trim();

    if (!name) return isAr ? "يرجى إدخال الاسم." : "Please enter your name.";
    if (!email) return isAr ? "يرجى إدخال البريد الإلكتروني." : "Please enter your email.";
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email))
      return isAr ? "يرجى إدخال بريد إلكتروني صالح." : "Please enter a valid email address.";
    if (!phone) return isAr ? "يرجى إدخال رقم الهاتف." : "Please enter your phone number.";
    if (!scheduled_at) return isAr ? "يرجى اختيار موعد الاستشارة." : "Please select a consultation date.";
    return "";
  };

  useEffect(() => {
    async function loadProfile() {
      const res = await safeApiCall(() => api.apiGet("me"));
      if (
        (res.status === "success" || !res.status) &&
        (res.user || res.id || res.email)
      ) {
        const userData = res.user || res;
        setUser(userData);
        setForm((prev) => ({
          ...prev,
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
        }));
        const role = String(userData.role || "").toLowerCase();
        const uid = Number(userData.id || 0);
        const admin = role === "admin" || uid === 1;
        setIsAdmin(admin);
        setPaymentMethod((pm) =>
          ["test_card", "test_paypal"].includes(pm) && !admin ? "card" : pm
        );
      }
    }
    loadProfile();
  }, []);

  useEffect(() => {
    async function loadConsultation() {
      try {
        setLoading(true);
        setMsg("");
        const consultationData = await getConsultationDetails(consultationRef);
        setConsultation(consultationData);
      } catch (err) {
        setMsg(err?.message || (isAr ? "فشل تحميل بيانات الاستشارة." : "Failed to load consultation."));
      } finally {
        setLoading(false);
      }
    }
    if (consultationRef) loadConsultation();
  }, [consultationRef, lang, isAr]);

  const consultationPaymentMethods = useMemo(() => {
    const methods = [
      { code: "card", label_en: "Pay by card (Stripe LIVE)", label_ar: "الدفع بالبطاقة (Stripe LIVE)" },
      { code: "test_card", label_en: "Test card (Stripe Test) - Admin", label_ar: "دفع تجريبي (Stripe Test) - Admin" },
      { code: "paypal", label_en: "Pay with PayPal LIVE", label_ar: "الدفع عبر PayPal LIVE" },
      { code: "test_paypal", label_en: "Test PayPal (Sandbox) - Admin", label_ar: "دفع تجريبي (PayPal Sandbox) - Admin" },
    ].map((method) => ({
      ...method,
      availability: getCheckoutMethodAvailability(user, "consultation", method.code),
    }));

    return methods.filter((method) => {
      if (["test_card", "test_paypal"].includes(method.code) && !isAdmin) return false;
      if (method.availability?.supported === false) return false;
      return true;
    });
  }, [isAdmin, user]);

  const hasAvailablePaymentMethods = consultationPaymentMethods.length > 0;
  const unavailablePaymentReason = useMemo(() => {
    if (hasAvailablePaymentMethods) return "";
    const methodReasons = [
      getCheckoutMethodAvailability(user, "consultation", "card")?.reason,
      getCheckoutMethodAvailability(user, "consultation", "paypal")?.reason,
      isAdmin ? getCheckoutMethodAvailability(user, "consultation", "test_card")?.reason : null,
      isAdmin ? getCheckoutMethodAvailability(user, "consultation", "test_paypal")?.reason : null,
    ].filter(Boolean);

    if (methodReasons.length > 0) return methodReasons[0];
    return isAr ? "لا توجد طرق دفع متاحة حاليًا." : "No payment methods are currently available.";
  }, [hasAvailablePaymentMethods, isAr, isAdmin, user]);

  useEffect(() => {
    if (consultationPaymentMethods.some((method) => method.code === paymentMethod)) return;

    const nextMethod = firstSupportedMethod(consultationPaymentMethods, "");
    if (nextMethod && nextMethod !== paymentMethod) setPaymentMethod(nextMethod);
  }, [consultationPaymentMethods, paymentMethod]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const resendVerification = async () => {
    if (resendLoading) return;
    const email = (form.email || "").trim();

    if (!email) {
      setVerifyBlock((p) => ({
        ...(p || {}),
        sent: false,
        error: isAr ? "البريد الإلكتروني غير موجود." : "Email is missing.",
      }));
      return;
    }

    setResendLoading(true);
    setMsg("");

    try {
      const res = await safeApiCall(() =>
        api.apiGet(`auth/resend_verification?email=${encodeURIComponent(email)}`)
      );

      if (res.status === "success") {
        setVerifyBlock((p) => ({ ...(p || {}), sent: true, error: null }));
        setMsg(res.message || (isAr ? "تمت إعادة إرسال رسالة التفعيل." : "Activation email resent."));
      } else {
        setVerifyBlock((p) => ({
          ...(p || {}),
          sent: false,
          error: res.message || (isAr ? "فشل إعادة إرسال رسالة التفعيل." : "Failed to resend activation email."),
        }));
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;

    setMsg("");
    setVerifyBlock(null);

    if (!consultation?.id) {
      pushMessage(isAr ? "بيانات الاستشارة غير موجودة." : "Consultation data is not available.");
      submitLock.current = false;
      return;
    }

    if (!hasAvailablePaymentMethods) {
      pushMessage(unavailablePaymentReason);
      submitLock.current = false;
      return;
    }

    const formError = validateCheckoutForm();
    if (formError) {
      pushMessage(formError);
      submitLock.current = false;
      return;
    }

    if (["test_card", "test_paypal"].includes(paymentMethod) && !isAdmin) {
      pushMessage(isAr ? "أوضاع الدفع التجريبية متاحة للأدمن فقط." : "Test payment modes are admin-only.");
      submitLock.current = false;
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        payment_method: paymentMethod,
        scheduled_at: toApiDateTime(form.scheduled_at),
        timezone: form.timezone,
        coins_to_use: Number(coinsData?.coins_to_use || 0),
      };

      const res = await safeApiCall(() => createConsultationCheckout(consultation.id, payload));

      const notVerified =
        res?.code === "ACCOUNT_NOT_VERIFIED" ||
        res?.body?.code === "ACCOUNT_NOT_VERIFIED";

      if (notVerified) {
        const r = res?.body || res;
        setVerifyBlock({
          message:
            r?.message ||
            (isAr
              ? "حسابك غير مفعّل. تم إرسال رسالة تفعيل إلى بريدك."
              : "Your account is not verified. We sent you a verification email."),
          email: form.email || "",
          sent: r?.email?.activation_sent ?? null,
          error: r?.email?.activation_error ?? null,
        });

        setMsg(
          r?.message ||
            (isAr
              ? "لا يمكن حجز الاستشارة قبل تفعيل الحساب."
              : "You must verify your account before booking a consultation.")
        );
        return;
      }

      if (res.status === "error") {
        pushMessage(res.message || (isAr ? "فشل إتمام الطلب." : "Checkout failed."));
        return;
      }

      if (res.data?.checkout_url) {
        window.dispatchEvent(new Event("mg:notifications-refresh"));
        window.location.href = res.data.checkout_url;
        return;
      }

      setMsg(
        isAr
          ? "تعذر بدء جلسة الدفع. لم يتم إنشاء الطلب بعد."
          : "Unable to start checkout session. No order has been created yet."
      );
    } catch (error) {
      pushMessage(
        isAr
          ? "حدث خطأ غير متوقع أثناء بدء الدفع."
          : "An unexpected error occurred while starting checkout."
      );
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  const consultationTitle = useMemo(() => getConsultationTitle(consultation, isAr), [consultation, isAr]);

  const breadcrumbItems = [
    { label: isAr ? "الاستشارات" : "Consultations", link: "/consultations" },
    { label: consultationTitle, link: `/consultation/${consultationRef}` },
    { label: isAr ? "إتمام الشراء" : "Checkout", link: "#" },
  ];

  if (loading) {
    return (
      <div className="checkout-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="container py-5">
          <LoadingScreen lang={lang} />
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page" dir={isAr ? "rtl" : "ltr"}>
      <Breadcrumb items={breadcrumbItems} className="breadcrumb-glass" />

      <div className="container py-4 py-md-5">
        <div className="checkout-page__header">
          <h1 className="checkout-page__title">{isAr ? "إتمام الشراء" : "Checkout"}</h1>
          <Link className="btn btn-outline-secondary rounded-pill" to={`/consultation/${consultationRef}`}>
            {isAr ? "العودة للاستشارة" : "Back to consultation"}
          </Link>
        </div>

        {verifyBlock && (
          <div className="checkout-alert checkout-alert--warning">
            <div className="checkout-alert__title">{isAr ? "حسابك غير مُفعّل" : "Account not verified"}</div>
            <div className="checkout-alert__text">{verifyBlock.message}</div>
            <div className="checkout-alert__email">
              {isAr
                ? `البريد: ${verifyBlock.email || form.email || "-"}`
                : `Email: ${verifyBlock.email || form.email || "-"}`}
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm rounded-pill"
              onClick={resendVerification}
              disabled={resendLoading}
            >
              {resendLoading
                ? isAr ? "جارٍ الإرسال..." : "Sending..."
                : isAr ? "إعادة إرسال رسالة التفعيل" : "Resend activation email"}
            </button>
            {verifyBlock.sent === true && (
              <div className="checkout-alert__success">
                {isAr ? "✅ تم إرسال رسالة التفعيل." : "✅ Activation email sent."}
              </div>
            )}
            {verifyBlock.error && <div className="checkout-alert__error">{verifyBlock.error}</div>}
          </div>
        )}

        {msg && (
          <div ref={messageRef} className="checkout-alert checkout-alert--info">
            {msg}
          </div>
        )}

        <div className="row g-4">
          <div className="col-lg-7">
            <div className="checkout-panel">
              <form onSubmit={handleSubmit} noValidate>
                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "الاسم" : "Name"}</label>
                  <input className="checkout-form-input" name="name" value={form.name} onChange={handleChange} required />
                </div>

                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "البريد الإلكتروني" : "Email"}</label>
                  <input className="checkout-form-input" name="email" type="email" value={form.email} onChange={handleChange} required />
                </div>

                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "الهاتف" : "Phone"}</label>
                  <input className="checkout-form-input" name="phone" value={form.phone} onChange={handleChange} required />
                </div>

                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "موعد الاستشارة" : "Consultation date"}</label>
                  <input
                    className="checkout-form-input"
                    type="datetime-local"
                    name="scheduled_at"
                    value={form.scheduled_at}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "المنطقة الزمنية" : "Timezone"}</label>
                  <input
                    className="checkout-form-input"
                    type="text"
                    name="timezone"
                    value={form.timezone}
                    onChange={handleChange}
                    placeholder={isAr ? "المنطقة الزمنية" : "e.g., America/New_York"}
                  />
                </div>

                <CoinsRedeemBox
                  subtotal={checkoutSubtotal}
                  onCoinsChange={setCoinsData}
                  disabled={submitting}
                  isAr={isAr}
                />

                <div className="checkout-form-group">
                  <label className="checkout-form-label">{isAr ? "طريقة الدفع" : "Payment method"}</label>

                  <div className="payment-methods">
                    {consultationPaymentMethods.map((method) => (
                      <button
                        key={method.code}
                        type="button"
                        className={`payment-method-btn ${paymentMethod === method.code ? "payment-method-btn--active" : ""}`}
                        onClick={() => setPaymentMethod(method.code)}
                      >
                        <span className="payment-method-btn__radio" />
                        <span className="payment-method-btn__label">
                          {isAr ? method.label_ar : method.label_en}
                        </span>
                      </button>
                    ))}
                  </div>

                  {!hasAvailablePaymentMethods && (
                    <div className="checkout-alert checkout-alert--warning mt-3 mb-0">
                      {unavailablePaymentReason}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-glow w-100 rounded-pill py-2 fw-semibold mt-3"
                  disabled={submitting || resendLoading || !hasAvailablePaymentMethods}
                >
                  {submitting
                    ? isAr ? "جارٍ المعالجة..." : "Processing..."
                    : isAr
                      ? `المتابعة إلى الدفع ($${checkoutGrandTotal.toFixed(2)})`
                      : `Continue to Payment ($${checkoutGrandTotal.toFixed(2)})`}
                </button>

                {verifyBlock && (
                  <div className="checkout-form-hint">
                    {isAr
                      ? "ملاحظة: لا يمكن حجز الاستشارة قبل تفعيل الحساب."
                      : "Note: You must verify your account before booking a consultation."}
                  </div>
                )}

                <div className="checkout-form-hint">
                  {isAr
                    ? paymentMethod === "paypal" || paymentMethod === "test_paypal"
                      ? "لن يتم حجز الاستشارة إلا بعد نجاح عملية الدفع عبر PayPal."
                      : "لن يتم حجز الاستشارة إلا بعد نجاح عملية الدفع."
                    : paymentMethod === "paypal" || paymentMethod === "test_paypal"
                      ? "The consultation will only be booked after successful PayPal payment."
                      : "The consultation will only be booked after successful payment."}
                </div>
              </form>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="checkout-summary">
              <h5 className="checkout-summary__title">{isAr ? "ملخص الطلب" : "Order Summary"}</h5>

              {consultation && (
                <div className="checkout-summary__item">
                  <div className="checkout-summary__item-name">{consultationTitle}</div>
                  <div className="checkout-summary__item-price">${checkoutSubtotal.toFixed(2)}</div>
                </div>
              )}

              <div className="checkout-summary__divider" />

              <div className="checkout-summary__row">
                <span>{isAr ? "سعر الاستشارة" : "Consultation price"}</span>
                <strong>${checkoutSubtotal.toFixed(2)}</strong>
              </div>

              <div className="checkout-summary__row checkout-summary__row--discount">
                <span>{isAr ? "خصم النقاط" : "Coins discount"}</span>
                <strong>- ${previewDiscount.toFixed(2)}</strong>
              </div>

              <div className="checkout-summary__row">
                <span>{isAr ? "رسوم المنصة (1%)" : "Platform fee (1%)"}</span>
                <strong>${platformFeeAmount.toFixed(2)}</strong>
              </div>

              <div className="checkout-summary__row">
                <span>{isAr ? "رسوم معالجة الدفع" : "Payment processing fee"}</span>
                <strong>${processorFeeAmount.toFixed(2)}</strong>
              </div>

              <div className="checkout-summary__row checkout-summary__row--total">
                <span>{isAr ? "الإجمالي المطلوب للدفع" : "Total due today"}</span>
                <strong className="checkout-summary__total">${checkoutGrandTotal.toFixed(2)}</strong>
              </div>

              <div className="checkout-form-hint mt-3">
                {isAr
                  ? "يشمل الإجمالي رسوم المنصة ورسوم معالجة الدفع حتى يظهر نفس المبلغ في Stripe."
                  : "The total includes platform and payment processing fees, so Stripe will show the same amount."}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}